/**
 * @fileoverview Syndicator Showdown — shared profile image resolution
 * @module utils/profileImage
 *
 * Single source of truth for "what avatar image should we show for this
 * user" — used by AccountPage, the AppShell sidebar, and the Dashboard
 * hero so all three surfaces always agree.
 */

export const DEFAULT_PROFILE_IMAGE = '/default-profile-pic.png';

export const MAX_AVATAR_BYTES = 5 * 1024 * 1024; // 5MB
export const ACCEPTED_AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

/**
 * Resolves the best available profile image URL for a user.
 *
 * Preference order: the users/{uid} Firestore doc (canonical, always up to
 * date once saved) — photoURL then avatarUrl for back-compat — then the
 * Firebase Auth profile's own photoURL (e.g. fresh Google sign-in before
 * the Firestore doc catches up), then the site default placeholder.
 *
 * Also works with a public player_profiles/{uid} doc (same field names),
 * which is what public-facing surfaces like leaderboards must hydrate from
 * — they cannot read another user's private users/{uid} doc.
 *
 * @param {Object|null|undefined} userDoc  - users/{uid} (or player_profiles/{uid}) data
 * @param {Object|null|undefined} authUser - firebase/auth currentUser
 * @returns {string}
 */
export function getProfileImageUrl(userDoc, authUser) {
  return userDoc?.photoURL
    || userDoc?.avatarUrl
    || authUser?.photoURL
    || DEFAULT_PROFILE_IMAGE;
}

/**
 * Resolves the public-facing identity for a player — used everywhere a
 * player is shown to OTHER users (leaderboards, shares, notifications,
 * player search). Never exposes displayName in preference to a chosen
 * public identity.
 *
 * Preference order: stableName (reserved field name, not yet in the
 * schema but checked first for forward-compat) → username (this is what
 * the Account page's "Stable Name" field actually writes today) →
 * displayName → the Firebase Auth profile's displayName → a generic
 * fallback. displayName stays LAST, not first — it's the private
 * account name, only shown publicly when the player hasn't set a
 * stable name / username yet.
 *
 * Works with users/{uid}, the public player_profiles/{uid} mirror, or a
 * denormalised leaderboard row — any object exposing these field names.
 *
 * @param {Object|null|undefined} userDoc  - users/{uid}, player_profiles/{uid}, or a leaderboard row
 * @param {Object|null|undefined} authUser - firebase/auth currentUser
 * @returns {string}
 */
export function getPublicName(userDoc, authUser) {
  return userDoc?.stableName
    || userDoc?.username
    || userDoc?.displayName
    || authUser?.displayName
    || 'Stable Owner';
}

/**
 * Validates a File selected for avatar upload.
 * @returns {string|null} an error message, or null if the file is valid.
 */
export function validateAvatarFile(file) {
  if (!file) return 'No file selected.';
  if (!ACCEPTED_AVATAR_TYPES.includes(file.type)) {
    return 'Please choose a PNG, JPG, or WEBP image.';
  }
  if (file.size > MAX_AVATAR_BYTES) {
    return 'Image must be under 5MB.';
  }
  return null;
}

/**
 * File extension to use for the Storage object, derived from the file's
 * MIME type (not the original filename, since users can rename freely).
 */
export function extensionForFile(file) {
  if (file.type === 'image/png') return 'png';
  if (file.type === 'image/webp') return 'webp';
  return 'jpg';
}
